import { Controller } from "stimulus";

export default class PollOptionsController extends Controller {
  static targets = ["list", "row", "input", "template"];
  static values = {
    max: Number,
    min: Number,
  };

  connect() {
    this.visibilityObserver = null;
    this.ensureMinimumRows();
    this.cleanupTrailingBlankRows();
    this.ensureGhostRow();
    this.reindex();
    this.updateGhostStyling();
    this.setupVisibilityObserver();
    this.applyVisibilityState();
  }

  disconnect() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
  }

  setupVisibilityObserver() {
    const container = this.visibilityContainer();
    if (!container) return;

    this.visibilityObserver = new MutationObserver(() => {
      this.applyVisibilityState();
    });

    this.visibilityObserver.observe(container, {
      attributes: true,
      attributeFilter: ["hidden", "class"],
    });
  }

  visibilityContainer() {
    return this.element.closest("[data-visibility-toggle-target='target']") || this.element.parentElement;
  }

  applyVisibilityState() {
    if (this.isHidden()) {
      this.disableControls();
    } else {
      this.restoreControls();
    }
  }

  disableControls() {
    this.rowTargets.forEach((row) => {
      const input = row.querySelector("input");
      if (!input) return;

      if (input.dataset.visibilityToggleOriginalDisabled === undefined) {
        input.dataset.visibilityToggleOriginalDisabled = input.disabled ? "true" : "false";
      }

      if (input.dataset.visibilityToggleOriginalRequired === undefined) {
        input.dataset.visibilityToggleOriginalRequired = input.required ? "true" : "false";
      }

      input.disabled = true;
      input.required = false;
    });
  }

  restoreControls() {
    this.rowTargets.forEach((row) => {
      const input = row.querySelector("input");
      if (!input) return;

      if (input.dataset.visibilityToggleOriginalDisabled !== undefined) {
        input.disabled = input.dataset.visibilityToggleOriginalDisabled === "true";
        delete input.dataset.visibilityToggleOriginalDisabled;
      }

      if (input.dataset.visibilityToggleOriginalRequired !== undefined) {
        input.required = input.dataset.visibilityToggleOriginalRequired === "true";
        delete input.dataset.visibilityToggleOriginalRequired;
      }
    });
  }

  isHidden() {
    const hiddenAncestor = this.element.closest("[hidden]");
    if (hiddenAncestor) return true;

    const classHiddenAncestor = this.element.closest(".hidden");
    return Boolean(classHiddenAncestor);
  }

  inputChanged() {
    this.cleanupTrailingBlankRows();
    this.ensureGhostRow();
    this.reindex();
    this.updateGhostStyling();
    this.applyVisibilityState();
  }

  inputBlurred(event) {
    const input = event.target;
    const row = input.closest("[data-poll-options-target='row']");
    if (!row) return;

    const value = input.value.trim();
    const rows = this.rowTargets;
    const isLastRow = rows[rows.length - 1] === row;

    if (value === "" && !isLastRow && rows.length > this.minValue) {
      row.remove();
      this.cleanupTrailingBlankRows();
      this.ensureMinimumRows();
      this.ensureGhostRow();
      this.reindex();
      this.updateGhostStyling();
      this.applyVisibilityState();
    }
  }

  ensureMinimumRows() {
    while (this.rowTargets.length < this.minValue) {
      this.appendRow("");
    }
  }

  ensureGhostRow() {
    const rows = this.rowTargets;
    if (rows.length === 0) {
      this.appendRow("");
      return;
    }

    if (rows.length >= this.maxValue) {
      return;
    }

    const lastInput = rows[rows.length - 1].querySelector("input");
    if (lastInput && lastInput.value.trim() !== "") {
      this.appendRow("");
    }
  }

  cleanupTrailingBlankRows() {
    let rows = this.rowTargets;
    while (rows.length > this.minValue + 1) {
      const lastRow = rows[rows.length - 1];
      const secondLastRow = rows[rows.length - 2];
      const lastVal = (lastRow.querySelector("input")?.value || "").trim();
      const secondLastVal = (secondLastRow.querySelector("input")?.value || "").trim();

      if (lastVal === "" && secondLastVal === "") {
        lastRow.remove();
        rows = this.rowTargets;
      } else {
        break;
      }
    }
  }

  // name/id/placeholder are intentionally left unset here: every call site invokes
  // reindex() immediately afterward, which computes them from final row position.
  appendRow(value) {
    if (this.rowTargets.length >= this.maxValue) return;

    const row = this.templateTarget.content.cloneNode(true).firstElementChild;

    const input = row.querySelector("input");
    if (input) {
      input.value = value;
    }

    this.listTarget.appendChild(row);
  }

  reindex() {
    this.rowTargets.forEach((row, idx) => {
      const input = row.querySelector("input");
      if (!input) return;

      input.name = `community_thread[poll_attributes][options_attributes][${idx}][label]`;
      input.id = `community_thread_poll_option_${idx}`;
      input.placeholder = `Option ${idx + 1}`;
      input.required = idx < this.minValue;
    });

    this.applyVisibilityState();
  }

  updateGhostStyling() {
    const rows = this.rowTargets;
    const lastIdx = rows.length - 1;

    rows.forEach((row, idx) => {
      const input = row.querySelector("input");
      if (!input) return;

      input.classList.add("transition-opacity");

      const isGhost = idx === lastIdx && input.value.trim() === "";
      input.classList.toggle("opacity-60", isGhost);
      input.classList.toggle("focus:opacity-100", isGhost);
    });
  }
}
